<script setup lang="ts">
import {computed, inject, onMounted, ref, watch, watchEffect} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {useStore} from 'vuex'
import AppNavbarMenu from '@/components/app/app-navbar/AppNavbarMenu.vue'
import {useBrandIcon} from '@/composables/useBrandIcon'
import {createShareService} from '@/services/share.service'
import {useContextStore} from '@/store/modules/context'
import {isStandalonePWA} from 'is-standalone-pwa'
import {Sport, SportData, useSport} from "@/composables/useSports";
import {useBrandLogo} from "@/composables/useBrandLogo";
import SportSelectorModal from '@/components/dialogs/sport-selector/SportSelectorModal.vue'
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { closeAllModalsEventBus } from '@/utils/eventBus';

const store = useStore()
const route = useRoute()
const shareService = createShareService(store)
const showSportSelectorModal = ref(false)
const contextStore = useContextStore()
const isPwa = isStandalonePWA()

closeAllModalsEventBus.on(() => {
  showSportSelectorModal.value = false
})

const context = computed(() => store.getters['context'])
const brandName = computed(() => store.getters['settingsModule/brandName'])

const appClientHome = computed(() => {
  const val = store.getters['configModule/appClientHome']
  return val ? val.trim() : ''
})

const brandLink = computed(() => {
  if (appClientHome.value) return appClientHome.value
  if (route.path.includes('odds')) return '/odds'
  if (route.path.includes('betpic')) return '/betpic'
  if (route.path.includes('bet')) return '/bet'
  return '/'
})

const isExternalBrandLink = computed(() => {
  return /^https?:\/\//i.test(brandLink.value)
})

function navigateToBrand() {
  if (isExternalBrandLink.value) {
    if (Capacitor.isNativePlatform()) {
      Browser.open(brandLink.value);
    } else {
      window.location.href = brandLink.value;
    }
  } else {
    router.push(brandLink.value);
  }
}

const betBuilderSport = ref<SportData | null>(null);
watchEffect(async () => {
  betBuilderSport.value = null;
  const isBuilderPath = route.path.includes('/bet/') || route.path.includes('/betpic/') || route.path.includes('/odds/')
  if (!isBuilderPath) return

  const contextName = route.params.context as string
  if (!contextName) return
    
  const contextInfo = contextStore.contextMap[contextName]
  let sport = null;
  if (contextInfo?.sports?.length === 1) sport = contextInfo.sports[0]
  if (!sport) return

  betBuilderSport.value = await useSport(sport);
});

const superSetSport = ref<SportData | null>(null);
watchEffect(async () => {
  let sport: Sport | null
  if (route.path.includes('supersetSoccer')) sport = 'SOCCER'
  else if (route.path.includes('supersetRacing')) sport = 'HORSE_RACING'
  else if (route.path.includes('supersetAmericanFootball')) sport = 'AMERICAN_FOOTBALL'
  else sport = null
  if (sport) {
    superSetSport.value = await useSport(sport);
  } else {
    superSetSport.value = null
  }
});

const brandImgSrc = ref<string>('')

watchEffect(async () => {
  const path = route.path
  const isBaseBet = /^\/bet\/?$/.test(path)
  const isBaseOdds = /^\/odds\/?$/.test(path)
  const isBaseBetPic = /^\/betpic\/?$/.test(path)

  if (!context.value || isBaseBet || isBaseOdds || isBaseBetPic || path.startsWith('/odds/')) {
    brandImgSrc.value = ''
    return
  }

  const logoData = shareService.getLogoData()
  if (!logoData == null) {
    console.log("Not logo data - contextMap not initialised")
    brandImgSrc.value = ''
    return
  }
  if (!logoData?.context) {
    brandImgSrc.value = ''
    return
  }

  brandImgSrc.value = await useBrandIcon({
    context: logoData.context,
    background: logoData.background,
    cornerRadius: 32
  })
})

const oddsLogoIcon = ref('')
const betLogoIcon = ref('')
const betpicLogoIcon = ref('')

onMounted(async () => {
  oddsLogoIcon.value = await useBrandLogo({ context: 'askodds', width: 600, background: 'transparent' })
  betLogoIcon.value = await useBrandLogo({ context: 'askbookmaker', width: 600, background: 'transparent' })
  betpicLogoIcon.value = await useBrandLogo({ context: 'betpic', width: 600, background: 'transparent' })
})

function isContextListPath(path: string): boolean {
  const p = path.replace(/\/+$/, '')
  return p === '/odds' || p === '/bet' || p === '/betpic'
}

function updateTitleAndFavicon(toPath: string, ctxParam?: string) {
  const contextStore = useContextStore()

  let ctx = ctxParam || ''
  if (!ctx) { 
    try {
      const storeRef = (require as any)('@/store').default || (require as any)('@/store/index').default
      ctx = storeRef?.getters?.context || ''
    } catch (_) {
    }
  }

  if (toPath === '/bet' || toPath === '/odds') {
    ctx = ''
  }

  const contextInfo = contextStore.contextMap[ctx] || {}

  const brandValue = contextInfo.brand
  const brandName = brandValue && store.getters['configModule/themes'][brandValue]?.name || ''

  const label = brandName
  let baseTitle = 'Voxbet'
  let favicon = 'favicon-voxbet.svg'

  if (isPwa) {
    document.title = ''
  } else {
    if (toPath.startsWith('/odds')) {
      baseTitle = 'Ask Odds'
      favicon = 'favicon-askodds.svg'
      document.title = baseTitle
    } else if (toPath.startsWith('/betpic')) {
      baseTitle = 'BetPic'
      favicon = 'favicon-betpic.svg'
      document.title = toPath === '/betpic' ? baseTitle : label || baseTitle
    } else if (toPath.startsWith('/bet')) {
      baseTitle = 'Ask Bookmaker'
      favicon = 'favicon-askbookmaker.svg'
      document.title = toPath === '/bet' ? baseTitle : label || baseTitle
    }
  }

  setFavicon(import.meta.env.BASE_URL + favicon)
}

function setFavicon(iconUrl: string) {
  let link: HTMLLinkElement | null = document.querySelector("link[rel*='icon']")
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  link.href = iconUrl
}

const router = useRouter()

onMounted(() => {
  updateTitleAndFavicon(router.currentRoute.value.path, router.currentRoute.value.params.context as string)
})

</script>

<template>
  <header class="fixed top-4 inset-x-0 z-50 h-16 container mx-auto bg-navbar/70 backdrop-blur-xl border border-white/10 rounded-2xl shadow-lg transition-all duration-300 max-md:top-0 max-md:w-full max-md:rounded-none max-md:border-x-0 max-md:border-t-0 max-md:h-14 max-md:bg-navbar/95">
    <div class="flex h-full items-center justify-between px-6 max-md:px-2">

      <!-- TEXT BRAND LINK -->
      <a
          v-if="isExternalBrandLink"
          href="#"
          @click.prevent="navigateToBrand"
          class="text-navbar-foreground hover:opacity-80 transition-opacity text-base font-bold mr-2 flex items-center h-full"
      >
        <img v-if="brandName == 'Ask Odds'" :src="oddsLogoIcon" class="h-full object-contain" alt="logo"/>
        <img v-else-if="brandName == 'Ask Bookmaker'" :src="betLogoIcon" class="h-full object-contain" alt="logo"/>
        <img v-else-if="brandName == 'BetPic'" :src="betpicLogoIcon" class="h-full object-contain" alt="logo"/>
        <span v-else class="text-xl tracking-tight">{{ brandName }}</span>
      </a>
 
      <RouterLink
          v-else
          :to="brandLink"
          class="text-navbar-foreground hover:opacity-80 transition-opacity text-base font-bold mr-2 flex items-center h-full"
      >
        <img v-if="brandName == 'Ask Odds'" :src="oddsLogoIcon" class="h-full object-contain" alt="logo"/>
        <img v-else-if="brandName == 'Ask Bookmaker'" :src="betLogoIcon" class="h-full object-contain" alt="logo"/>
        <img v-else-if="brandName == 'BetPic'" :src="betpicLogoIcon" class="h-full object-contain" alt="logo"/>
        <span v-else class="text-xl tracking-tight">{{ brandName }}</span>
      </RouterLink>

      <div class="flex items-center gap-1">
        <a v-if="superSetSport"
           href="#"
           class="h-14 w-14 p-1 rounded-xl flex items-center justify-center text-2xl select-none bg-accent/10 border border-accent/20"
           @click.prevent="showSportSelectorModal = true"
        >
          <img :src="superSetSport.iconSvg" :alt="superSetSport.label" class="w-full h-full object-contain"/>
        </a>

        <!-- IMAGE BRAND LINK -->
        <RouterLink
            v-if="brandImgSrc && !appClientHome"
            :to="brandLink"
            class="h-14 w-14 p-1 rounded-xl flex-shrink-0 bg-white/5 border border-white/10"
        >
          <img
              :src="brandImgSrc"
              :alt="brandName"
              class="h-full w-full object-contain rounded-lg"
          />
        </RouterLink>

        <a
            v-else-if="brandImgSrc && appClientHome"
            href="#"
            class="h-14 w-14 p-1 rounded-xl flex-shrink-0 bg-white/5 border border-white/10"
            @click.prevent="navigateToBrand"
        >
          <img
              :src="brandImgSrc"
              :alt="brandName"
              class="h-full w-full object-contain rounded-lg"
          />
        </a>

        <a
            v-if="betBuilderSport && !superSetSport"
            href="#"
            class="h-14 w-14 p-1 rounded-xl flex items-center justify-center text-2xl select-none bg-accent/10 border border-accent/20"
            @click.prevent="showSportSelectorModal = true"
        >
          <img :src="betBuilderSport.iconSvg" :alt="betBuilderSport.label" class="w-full h-full object-contain"/>
        </a>
      </div>

      <div class="flex flex-1 justify-end">
        <AppNavbarMenu/>
      </div>
    </div>
  </header>

  <SportSelectorModal
      v-model:isOpen="showSportSelectorModal"
  />
</template>